import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileText, FileSpreadsheet, Download } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { value: "hpp", label: "Laporan HPP per Batch" },
  { value: "environmental", label: "Laporan Biaya Lingkungan" },
  { value: "carbon", label: "Laporan Emisi Karbon" },
  { value: "comparison", label: "Laporan Perbandingan Produk" },
  { value: "material-efficiency", label: "Laporan Efisiensi Material" },
  { value: "ai", label: "Laporan AI Rekomendasi" },
  { value: "selling-price", label: "Laporan Harga Jual" },
];

export default function Reports() {
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [type, setType] = useState("hpp");
  const [downloading, setDownloading] = useState(false);
  const [recent, setRecent] = useState([]);
  const [productFilter, setProductFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadRecent = () => api.get("/reports").then((r) => setRecent(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/batches").then((r) => { setBatches(r.data); if (r.data[0]) setBatchId(r.data[0].id); });
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    loadRecent();
  }, []);

  const filteredBatches = useMemo(() => {
    return batches.filter((b) => {
      if (productFilter !== "all" && b.product_id !== productFilter) return false;
      if (dateFrom && (b.tanggal || "") < dateFrom) return false;
      if (dateTo && (b.tanggal || "") > dateTo) return false;
      return true;
    });
  }, [batches, productFilter, dateFrom, dateTo]);

  // Keep selected batch valid when filters change
  useEffect(() => {
    if (filteredBatches.length === 0) {
      setBatchId("");
    } else if (!filteredBatches.some((b) => b.id === batchId)) {
      setBatchId(filteredBatches[0].id);
    }
  }, [filteredBatches, batchId]);

  const download = async (format) => {
    if (!batchId) return toast.error("Pilih batch terlebih dahulu");
    setDownloading(true);
    try {
      const res = await api.get("/reports/generate", {
        params: { type, batch_id: batchId, format },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Laporan ${format.toUpperCase()} berhasil diunduh`);
      loadRecent();
    } catch (e) {
      toast.error("Gagal membuat laporan");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold text-slate-900">Reports</h2>
        <p className="text-sm text-slate-500 mt-1">Hasilkan laporan manajemen dalam format PDF dan Excel.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 border-slate-200 shadow-sm">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Buat Laporan</h3>

          {/* Filters */}
          <div className="grid sm:grid-cols-3 gap-4 mb-4 p-4 rounded-lg bg-slate-50 border border-slate-200">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">Filter Produk</Label>
              <Select value={productFilter} onValueChange={setProductFilter}>
                <SelectTrigger data-testid="report-product-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Produk</SelectItem>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">Dari Tanggal</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">Sampai Tanggal</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">Jenis Laporan</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="report-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">Batch Produksi ({filteredBatches.length} batch)</Label>
              <Select value={batchId} onValueChange={setBatchId}>
                <SelectTrigger data-testid="report-batch-select"><SelectValue placeholder="Tidak ada batch" /></SelectTrigger>
                <SelectContent>
                  {filteredBatches.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-slate-400">Tidak ada batch sesuai filter</div>
                  ) : filteredBatches.map((b) => <SelectItem key={b.id} value={b.id}>{b.no_batch} — {b.product_name} ({b.tanggal})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <Button onClick={() => download("pdf")} disabled={downloading || !batchId} className="bg-red-600 hover:bg-red-700" data-testid="download-pdf-btn">
              <FileText className="h-4 w-4 mr-1.5" /> Unduh PDF
            </Button>
            <Button onClick={() => download("excel")} disabled={downloading || !batchId} className="bg-green-700 hover:bg-green-800" data-testid="download-excel-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Unduh Excel
            </Button>
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Laporan Terbaru</h3>
          {recent.length === 0 ? <p className="text-sm text-slate-400">Belum ada laporan.</p> : (
            <div className="space-y-2">
              {recent.map((r) => (
                <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50">
                  <Download className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.title}</p>
                    <p className="text-[11px] text-slate-400">{(r.format || "").toUpperCase()} · {(r.created_at || "").slice(0, 10)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6 border-slate-200 shadow-sm">
        <h3 className="font-heading font-semibold text-slate-900 mb-3">Jenis Laporan Tersedia</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TYPES.map((t) => (
            <div key={t.value} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
              <div className="h-9 w-9 rounded-lg bg-green-50 text-green-700 flex items-center justify-center shrink-0">
                <FileText className="h-4 w-4" />
              </div>
              <span className="text-sm text-slate-700">{t.label}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
